<script setup lang="ts">
import { computed, type PropType } from 'vue';
import {
  gaiaChipDot,
  gaiaFonts,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
} from '../themes/tokens';
import { PREVIEW_LETTER_SPACING_EM, PROVIDER_LINE_HEIGHT } from '@/utils/signatureFieldText';

const props = defineProps({
  /** Field type label, e.g. "Signature" — adapt SignatureFieldType via statusMaps. */
  label: {
    type: String,
    default: undefined,
  },
  /** Who fills it. */
  who: {
    type: String,
    default: undefined,
  },
  /** The signer's colour (gaiaCategoryPalette, positional per signer). */
  color: {
    type: String,
    required: true,
  },
  /**
   * Geometry, as CSS lengths. Pass percentages: the overlay stores field
   * positions as a share of the page, so a box keeps its place when the page
   * is re-rendered at another width.
   */
  left: {
    type: String,
    required: true,
  },
  top: {
    type: String,
    required: true,
  },
  width: {
    type: String,
    default: '22%',
  },
  height: {
    type: String,
    default: '6%',
  },
  /**
   * A text field's text (mention, question, "Name", …). When set, the
   * box shows it as the PDF will — top-aligned at 1.5 line height, since the
   * provider cannot centre it — and the signer moves to a corner badge.
   */
  text: {
    type: String as PropType<string | null>,
    default: null,
  },
  /** `text` is a sample of what will be filled in (a name, a date), not fixed text: drawn quieter. */
  sample: {
    type: Boolean,
    default: false,
  },
  /** `text` doesn't fit the box at its font size: the border turns amber and dashed. */
  overflow: {
    type: Boolean,
    default: false,
  },
  /**
   * Whole lines the box holds at its font size, as the provider prints it: only
   * lines that fully fit, and a one-line box doesn't wrap — the text runs on and
   * is cut at the edge. Null (unmeasured) draws the text unclipped.
   */
  lines: {
    type: Number as PropType<number | null>,
    default: null,
  },
  /** CSS font size for `text`: the field's pt size at the page's current scale. */
  fontSize: {
    type: String,
    default: undefined,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  /**
   * The rectangle being dragged out on an empty part of the page, before the
   * field exists — dashed, label-less and click-through.
   */
  preview: {
    type: Boolean,
    default: false,
  },
});

/**
 * `select` and `remove` are the clicks. Moving is a drag on the box itself, so
 * the view listens for `mousedown` through attrs fallthrough (as the Kanban
 * board does for card drags) and runs the geometry; the grip has its own event
 * because it must not also start a move.
 */
const emit = defineEmits<{ select: []; remove: []; 'resize-start': [event: MouseEvent] }>();

const surface = gaiaNeutral.surface;

// One input, three strengths: a tinted fill, the solid border, and the
// selected ring.
const fill = computed(() => `color-mix(in srgb, ${props.color} 10%, transparent)`);
// The badge is the same tint made opaque, so it hides the text under it
// without reading as a separate white chip.
const badgeFill = computed(() => `color-mix(in srgb, ${props.color} 10%, ${surface})`);
const ring = computed(() => `0 0 0 2px color-mix(in srgb, ${props.color} 45%, transparent)`);

const textInk = gaiaNeutral.ink;
const sampleInk = gaiaNeutral.textSubtle;
const overflowInk = gaiaChipDot.warning;
// The provider's face is monospaced (see `signatureFieldText`); the preview
// draws in the app's mono, tightened to the same glyph width.
const monoFont = gaiaFonts.mono;
const letterSpacing = `${PREVIEW_LETTER_SPACING_EM}em`;
const lineHeight = String(PROVIDER_LINE_HEIGHT);
// The grip's ribs read as a texture rather than a colour, so they take the
// disabled-ink token — the same value the app's own overlay draws them in.
const gripInk = gaiaNeutral.inkDisabled;
// The field's label is drawn in its signer's colour and pinned inside the
// box, so its type is a selector-bound rule rather than a GaiaText prop.
const labelSize = gaiaFontSize.nano;
const bold = String(gaiaFontWeight.bold);
</script>

<template>
  <!-- A signature field on the document page (wizard step 4). The page canvas
       owns the coordinates, the move/draw maths and the crosshair; this is the
       box: tinted in its signer's colour, grab-cursored, with the corner grip
       that starts a resize. -->
  <n-flex
    vertical
    justify="flex-start"
    :size="0"
    class="gaia-placed-field"
    :class="{ selected: props.selected, preview: props.preview, overflow: props.overflow }"
    :style="{
      left: props.left,
      top: props.top,
      width: props.width,
      height: props.height,
      borderColor: props.overflow ? overflowInk : props.color,
      '--field-color': props.color,
      background: fill,
      boxShadow: props.selected ? ring : undefined,
    }"
    :title="
      props.preview
        ? undefined
        : [props.label, props.who, props.overflow ? 'doesn’t fit the box' : '']
            .filter(Boolean)
            .join(' · ')
    "
    @click="emit('select')"
  >
    <template v-if="!props.preview">
      <span
        v-if="props.text !== null"
        class="field-text"
        :class="{ sample: props.sample, 'single-line': props.lines === 1 }"
        :style="{
          fontSize: props.fontSize,
          maxHeight: props.lines === null ? undefined : `${props.lines * PROVIDER_LINE_HEIGHT}em`,
        }"
      >
        {{ props.text }}
      </span>
      <n-flex
        align="center"
        :size="6"
        :wrap="false"
        :class="props.text !== null ? 'badge' : 'chip'"
      >
        <span class="dot" :style="{ background: props.color }" />
        <span class="label" :style="{ color: props.color }">
          <template v-if="props.text !== null">{{ props.who }}</template>
          <template v-else>
            {{ props.label }}<template v-if="props.who"> · {{ props.who }}</template>
          </template>
        </span>
        <button
          type="button"
          class="remove"
          :style="{ color: props.color }"
          :aria-label="`Remove ${props.label} field`"
          @click.stop="emit('remove')"
          @mousedown.stop
        >
          <svg
            width="9"
            height="9"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          >
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </n-flex>
      <!-- Bottom-right grip. `.stop` keeps the resize from also starting a move. -->
      <span
        class="grip"
        role="button"
        aria-label="Resize field"
        @mousedown.stop="emit('resize-start', $event)"
      />
    </template>
  </n-flex>
</template>

<style scoped>
/* Border-box so a percentage width stays the box's real width once the 1px
   border is added. Not overflow-hidden: a box too small for its label (a
   checkbox) hangs the label above itself, below. The label and the text clip
   themselves instead. A size container, so that switch is CSS. */
.gaia-placed-field {
  position: absolute;
  box-sizing: border-box;
  border: 1px solid;
  border-radius: 4px;
  container-type: size;
  user-select: none;
  /* The page it sits on is set in the heading serif; the field is UI. */
  font-family: inherit;
  cursor: grab;
  transition: box-shadow 0.12s ease;
}

.gaia-placed-field:active {
  cursor: grabbing;
}

/* The draw rectangle: no content, and it must not eat the mousemove that is
   still drawing it. */
.gaia-placed-field.preview {
  border-style: dashed;
  border-width: 2px;
  pointer-events: none;
}

/* The field's text as the PDF will set it: top-left, the provider's line
   height and glyph width, no inset, wrapping by word. Falls back to the label
   size until the page is measured. */
.field-text {
  display: block;
  min-height: 0;
  font-family: v-bind(monoFont);
  font-size: v-bind(labelSize);
  letter-spacing: v-bind(letterSpacing);
  line-height: v-bind(lineHeight);
  color: v-bind(textInk);
  overflow: hidden;
  overflow-wrap: anywhere;
}

/* One line's room: the provider runs the text on and cuts it at the edge. */
.field-text.single-line {
  white-space: nowrap;
}

/* A sample of what gets filled in, not text on the page yet. */
.field-text.sample {
  color: v-bind(sampleInk);
}

/* Text that won't fit: the box says so with its own border. */
.gaia-placed-field.overflow {
  border-style: dashed;
}

/* Text fields: the signer's dot and initials sit in the top-right corner over
   the text, on the field's own tint so it blends into the box. */
.badge {
  position: absolute;
  top: 1px;
  right: 1px;
  padding: 1px 3px;
  border-radius: 3px;
  background: v-bind(badgeFill);
}

.badge .label {
  flex: none;
}

/* A non-text field's label row, inside the box's top-left corner. */
.chip {
  padding: 3px 5px;
  min-width: 0;
}

/* Too small to hold its label: the label leaves the box and sits on top of
   it as a tag in the signer's colour, unclipped. */
@container (max-width: 80px) or (max-height: 18px) {
  .chip {
    position: absolute;
    bottom: calc(100% + 3px);
    left: -1px;
    padding: 1px 5px;
    border: 1px solid var(--field-color);
    border-radius: 4px;
    background: v-bind(badgeFill);
    white-space: nowrap;
  }

  .chip .label {
    flex: none;
  }
}

.dot {
  width: 6px;
  height: 6px;
  flex: none;
  border-radius: 50%;
}

.label {
  min-width: 0;
  flex: 1;
  font-size: v-bind(labelSize);
  font-weight: v-bind(bold);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove {
  width: 14px;
  height: 14px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 3px;
  background: none;
  cursor: pointer;
  opacity: 0;
}

/* The ✕ stays out of the way until the field is pointed at or selected —
   a page of fields shouldn't be a page of close buttons. */
.gaia-placed-field:hover .remove,
.gaia-placed-field.selected .remove {
  opacity: 1;
}

.remove:hover {
  background: v-bind(surface);
}

/* The diagonal-ribs grip the app's own overlay uses, so the affordance reads
   the same in both places. */
.grip {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 12px;
  height: 12px;
  cursor: se-resize;
  border-radius: 0 0 3px 0;
  background: linear-gradient(
    135deg,
    transparent 40%,
    v-bind(gripInk) 40%,
    v-bind(gripInk) 55%,
    transparent 55%,
    transparent 65%,
    v-bind(gripInk) 65%,
    v-bind(gripInk) 80%,
    transparent 80%
  );
  opacity: 0;
}

.gaia-placed-field:hover .grip,
.gaia-placed-field.selected .grip {
  opacity: 1;
}
</style>
